import { memo, useCallback } from 'react';
import { Dropdownlist } from '@/components/Dropdownlist';
import type { FormSearchPredicateData } from '@/services/api-types';
import type { FormFilterBaseProps } from './filter-base';

export interface SelectFilterProps extends FormFilterBaseProps {}

/**
 * Dropdown filter over a select field's option list. Same UI/behavior as
 * RadioFilter — kept as a separate component so callers can dispatch by
 * field type and the two can evolve independently (e.g. multi-select
 * support on `select` later). Emits `{op: 'EQ', value: selected}` when an
 * option is picked, or `[]` when cleared.
 */
export const SelectFilter = memo(function SelectFilter({
  field,
  predicates,
  onPredicatesChange,
}: SelectFilterProps) {
  const existing = predicates.find((p) => p.op === 'EQ');
  const currentValue = existing && typeof existing.value === 'string' ? existing.value : null;

  const handleChange = useCallback(
    (val: string | number | null) => {
      if (val == null || val === '') {
        onPredicatesChange([]);
        return;
      }
      const predicate: FormSearchPredicateData = {
        fieldKey: field.key,
        op: 'EQ',
        value: val as string,
      };
      onPredicatesChange([predicate]);
    },
    [field.key, onPredicatesChange],
  );

  const options = field.options ?? [];

  return (
    <div className="field">
      <label htmlFor={`form-filter-${field.key}`}>{field.label}</label>
      <Dropdownlist
        value={currentValue}
        items={options}
        sortItems={false}
        searchable={false}
        clearable
        placeholder={`Any ${field.label.toLowerCase()}`}
        onChange={handleChange}
      />
    </div>
  );
});
